import { useTenantApi } from '@/account/hook/useTenantApi';
import { TicketType } from '@/api/constant';
import { type CartItem, type CartItemSaveRequest, cartQueryKey, getProductType, registrationQueryKey, saveCartItem, type TicketData } from '@/api/registration';
import BaseBlockList from '@/base/BlockList';
import MessageBarInfo from '@/base/MessageBar/Info';
import {
  UiBox,
  UiButton,
  UiDrawer,
  UiDrawerBody,
  UiDrawerCloseButton,
  UiDrawerContent, UiDrawerFooter, UiDrawerHeader,
  UiDrawerOverlay,
  type UiDrawerProps,
  UiHStack,
  UiIconCurrencyCircleDollar, UiIconPencilSimple, UiIconShoppingCart,
  UiIconStackSimple,
  UiIconTicket,
  UiIconWarningDiamond,
  UiStack,
  uiStyles, UiTag,
  UiText,
} from '@/lib/ui';
import { useCartQuery } from '@/registration/hook/useCartQuery';
import { useIsAlreadyInCart } from '@/registration/hook/useIsAlreadyInCart';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { useRegistrationQuery } from '@/registration/hook/useRegistrationQuery';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type FC, useCallback, useMemo } from 'react';
import BaseDividerHorizontal from '@/base/Divider/Horizontal';
import BaseButtonIconText from '@/base/Button/IconText';
import { useShoppingCartToggle } from '@/registration/component/Register/ShoppingCart/ShoppingCartToggleProvider';
import BaseThemeButton from '@/base/ThemeButton';

export interface TicketDrawerProps extends Omit<UiDrawerProps, 'children'> {
  price: string
  ticket: TicketData
  isOpen: boolean
  onClose: () => void
  isAlreadyPurchased?: boolean
  displayCapacities?: boolean
}

const TicketDrawer: FC<TicketDrawerProps> = ({
  ticket,
  price,
  isOpen,
  onClose,
  isAlreadyPurchased = false,
  displayCapacities = true,
  ...props
}) => {
  const queryClient = useQueryClient();
  const { eventId } = useRegisterRoute();
  const registrationQuery = useRegistrationQuery(eventId);
  const registration = registrationQuery?.data;
  const { data: cart } = useCartQuery(eventId);
  const { createTenantApiRequest } = useTenantApi();
  const isAlreadyInCart = useIsAlreadyInCart(ticket);
  const { onOpen: onShoppingCartOpen } = useShoppingCartToggle();

  const { mutate, isLoading } = useMutation<CartItem, Error, CartItemSaveRequest>(
    async (params: CartItemSaveRequest) => {
      const response = await saveCartItem(createTenantApiRequest)(params);
      return response.items?.[0];
    },
    {
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries([cartQueryKey, { registrationId: registration?.id }]),
          queryClient.invalidateQueries({ queryKey: [registrationQueryKey, { eventId }] }),
        ]);
        onClose();
      }
    }
  );

  const mainTicketExist = useMemo(() => {
    if (!['Main', 1].includes(ticket.type)) return false;
    return cart?.cartItems.find((cartItem) => { return cartItem.productType === 'DelegateType'; });
  }, [cart, ticket]);

  const addToCart = useCallback(() => {
    if (!cart) {
      return;
    }

    const replaceRegistrationType = mainTicketExist && mainTicketExist.id !== ticket.id;

    mutate({
      productId: ticket.id,
      productType: getProductType(ticket.type),
      quantity: 1,
      cartId: cart.id,
      replaceRegistrationType
    });
  }, [cart, mainTicketExist, mutate, ticket.id, ticket.type]);

  return (
    <UiDrawer placement={'right'} size={'lg'} isOpen={isOpen} onClose={onClose}>
      <UiDrawerOverlay />
      <UiDrawerContent bgColor={'gray.100'}>
        <UiDrawerCloseButton size={'lg'} fontWeight={'bold'} color={'primary.500'} />
        <UiDrawerHeader
          p={0}
        >
          <UiStack
            flexGrow={1}
            px={{base: 4, lg: 8}}
            // py={8}
            pt={{base: 4, lg: 8}}
            bgColor={'gray.100'}
            alignItems={'flex-start'}
          >
            <UiHStack
              // px={8}
              // py={4}
              // borderRadius={uiStyles.buttonRadius}
              // borderWidth={'1px'}
              // borderStyle={'solid'}
              // borderColor={'primary.500'}
              // justifyContent={'space-around'}
              onClick={onShoppingCartOpen}
              {...uiStyles.hover}
            >
              {/*<BaseButtonIconText*/}
              {/*  Icon={UiIconShoppingCart}*/}
              {/*  // variant={'large'}*/}
              {/*  // onClick={toModify}*/}
              {/*>*/}
              {/*  Shopping cart*/}
              {/*</BaseButtonIconText>*/}
              <BaseThemeButton
                theme={'primary'}
                leftIcon={(<UiIconShoppingCart size={'xl'} color={'primary.500'}/>)}
              >
                Shopping cart
              </BaseThemeButton>
            </UiHStack>
          </UiStack>
        </UiDrawerHeader>
        <BaseDividerHorizontal height={{base: 4, lg: 8}} />
        <UiDrawerBody px={{ base: 4, lg: 8 }} bgColor={'gray.100'} pb={16}>
          <UiStack
            justifyContent={'flex-start'}
            alignItems={'stretch'}
            flexGrow={1}
            spacing={6}
            borderRadius={uiStyles.borderRadius}
          >
            { ticket.type === TicketType.Main && (
              <MessageBarInfo borderRadius={uiStyles.borderRadius}>
                Only one registration type can be purchased for each attendee.
              </MessageBarInfo>
            )}
            <UiStack
              // px={{ base: 6, lg: 8 }}
              flexGrow={1}
              spacing={8}
              alignItems={'stretch'}
              // bgColor={'#fff'}
            >
              <UiStack
                borderRadius={uiStyles.borderRadius}
                bgColor={'#fff'}
                shadow={uiStyles.cardShadow}
                spacing={0}
                py={8}
                px={{base: 4, lg: 8}}
              >
                <UiStack>
                  <UiStack>
                    <UiText fontWeight={'bold'}>{ticket.name}</UiText>
                  </UiStack>
                  {!!ticket.description && (
                    <UiHStack>
                      <UiText color={'text.secondary'}>{ticket.description}</UiText>
                    </UiHStack>
                  )}
                </UiStack>
                <BaseDividerHorizontal height={6} />
                <UiStack spacing={4}>
                  <UiHStack>
                    <UiIconCurrencyCircleDollar size={'2xl'} color={'text.primary'} />
                    <UiText flexGrow={1} pl={2}>
                      Price
                    </UiText>
                    <UiText>
                      {price}
                    </UiText>
                  </UiHStack>
                  {displayCapacities && (
                    <UiHStack>
                      <UiIconStackSimple size={'2xl'} color={'text.primary'} />
                      <UiText flexGrow={1} pl={2}>
                        Tickets (available)
                      </UiText>
                      <UiText>
                        {ticket.currentStock}
                      </UiText>
                    </UiHStack>
                  )}
                </UiStack>
              </UiStack>
            </UiStack>
            <UiHStack
              // justifyContent={'flex-end'}
              // px={8}
              // pt={4}
            >
              {
                isAlreadyInCart ? (
                  <UiStack flexGrow={1} alignItems={'flex-end'}>
                    <UiHStack
                      borderRadius={uiStyles.borderRadius}
                      bgColor={'green.200'}
                      p={2}
                      px={4}
                    >
                      <UiIconWarningDiamond color={'green.800'} />
                      <UiText color={'green.800'}>Already in cart</UiText>
                    </UiHStack>
                  </UiStack>
                ) : mainTicketExist ? (
                  <UiStack
                    bgColor={'#fff'}
                    flexGrow={1}
                    px={{base: 4, lg: 8}}
                    py={{base: 4, lg: 8}}
                    borderRadius={uiStyles.borderRadius}
                  >
                    <UiStack spacing={4}>
                      <UiHStack alignItems={'center'} spacing={1} justifyContent={'space-between'}>
                        <UiText>Replace this (currently in the cart) ?</UiText>
                      </UiHStack>
                      <UiHStack bgColor={'green.100'} px={{base: 4, lg: 8}} py={4} borderRadius={uiStyles.borderRadius}>
                        <UiText>{mainTicketExist.name}</UiText>
                        {/*<UiTag colorScheme={'green'} p={4} py={2}>{mainTicketExist.name}</UiTag>*/}
                      </UiHStack>
                    </UiStack>
                    {/*<BaseDividerHorizontal height={4} />*/}
                    {/*<UiHStack justifyContent={'flex-end'}>*/}
                    {/*  <UiButton*/}
                    {/*    isDisabled={ticket.stock < 1}*/}
                    {/*    isLoading={isLoading}*/}
                    {/*    onClick={addToCart}*/}
                    {/*  >*/}
                    {/*    Yes*/}
                    {/*  </UiButton>*/}
                    {/*</UiHStack>*/}
                  </UiStack>
                ) : (
                  // <UiHStack justifyContent={'flex-end'} flexGrow={1}>
                  //   <UiButton
                  //     cursor={'pointer'}
                  //     as={'button'}
                  //     colorScheme={'primary'}
                  //     alignItems={'center'}
                  //     justifyContent={'center'}
                  //     size={'lg'}
                  //     isDisabled={ticket.stock < 1 || isAlreadyPurchased}
                  //     isLoading={isLoading}
                  //     onClick={addToCart}
                  //   >
                  //     {ticket.stock < 1 ? 'Unavailable' : 'Add to cart'}
                  //   </UiButton>
                  // </UiHStack>
                  <UiBox/>
                )
              }
            </UiHStack>
          </UiStack>
        </UiDrawerBody>
        <UiDrawerFooter p={0}>
          <UiStack
            flexGrow={1}
            px={{base: 4, lg: 8}}
            py={{base: 4, lg: 8}}
            bgColor={'gray.100'}
          >
            {isAlreadyInCart ? (
              <UiBox/>
            ) : mainTicketExist ? (
              <UiHStack justifyContent={'flex-end'}>
                <UiButton
                  isDisabled={ticket.stock < 1}
                  isLoading={isLoading}
                  onClick={addToCart}
                >
                  Replace
                </UiButton>
              </UiHStack>
            ) : (
              <UiHStack justifyContent={'flex-end'} flexGrow={1}>
                <UiButton
                  cursor={'pointer'}
                  as={'button'}
                  colorScheme={'primary'}
                  alignItems={'center'}
                  justifyContent={'center'}
                  size={'lg'}
                  isDisabled={ticket.stock < 1 || isAlreadyPurchased}
                  isLoading={isLoading}
                  onClick={addToCart}
                >
                  {ticket.stock < 1 ? 'Unavailable' : 'Add to cart'}
                </UiButton>
              </UiHStack>
            )}
          </UiStack>
        </UiDrawerFooter>
      </UiDrawerContent>
    </UiDrawer>
  );
};

export default TicketDrawer;
